<template>
  <div class="page-container">
    <main class="main-content">
      <div class="page-inner-box">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
          <el-breadcrumb-item>所有班级</el-breadcrumb-item>
        </el-breadcrumb>

        <div class="category-tabs" v-if="categoryList.length > 0">
          <span 
            class="tab-item" 
            :class="{ active: !query.categoryId }"
            @click="handleCategoryChange(null)"
          >全部</span>
          <span 
            v-for="cat in categoryList" 
            :key="cat.id"
            class="tab-item"
            :class="{ active: query.categoryId === cat.id }"
            @click="handleCategoryChange(cat.id)"
          >{{ cat.categoryName }}</span>
        </div>

        <div class="toolbar">
          <el-input
            v-model="query.className"
            size="small"
            clearable
            placeholder="搜索班级"
            class="search-input"
            @keyup.enter.native="handleSearch"
          />
          <el-button type="primary" size="small" @click="handleSearch">搜索</el-button>
        </div>

        <div class="list-body" v-loading="loading">
          <div v-if="classes.length > 0" class="grid">
            <el-card v-for="cls in classes" :key="cls.id" class="card ui-card" shadow="always" @click.native="goDetail(cls)">
              <div class="thumb">
                <img :src="cls.coverImage" alt="cover" />
              </div>
              <div class="title">{{ cls.className }}</div>
              <div class="subtitle">{{ cls.subtitle }}</div>
              <div class="meta">
                <span class="price">{{ cls.priceText }}</span>
                <span class="info">
                  <span class="course-count">{{ cls.courseCount }}门课程</span>
                  <span class="paper-count">{{ cls.paperCount }}套试卷</span>
                </span>
              </div>
              <div class="sales">
                <span>{{ cls.salesCount }}人已购</span>
              </div>
            </el-card>
          </div>

          <el-empty v-else-if="!loading" description="暂无班级" />
        </div>

        <div v-if="total > query.pageSize" class="pager">
          <el-pagination
            :current-page.sync="query.pageNum"
            :page-size="query.pageSize"
            layout="prev, pager, next"
            :total="total"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { getClassList, getClassCategoryTree } from '@/api/classApi'

export default {
  name: 'ClassList',
  data() {
    return {
      loading: false,
      total: 0,
      classes: [],
      categoryList: [],
      query: {
        categoryId: null,
        className: '',
        pageNum: 1,
        pageSize: 10
      }
    }
  },
  created() {
    const className = String((this.$route && this.$route.query && this.$route.query.className) || '').trim()
    if (className) this.query.className = className
    const categoryId = this.$route.query.categoryId
    if (categoryId) this.query.categoryId = Number(categoryId)
    this.fetchCategories()
    this.fetchClasses()
  },
  watch: {
    '$route.query.className'(next) {
      const className = String(next || '').trim()
      if (className === this.query.className) return
      this.query.className = className
      this.query.pageNum = 1
      this.fetchClasses()
    },
    '$route.query.categoryId'(next) {
      this.query.categoryId = next ? Number(next) : null
      this.query.pageNum = 1
      this.fetchClasses()
    }
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await getClassCategoryTree()
        if (res && res.code === 200 && res.data) {
          this.categoryList = res.data || []
        }
      } catch (err) {
        console.error('获取班级分类失败', err)
      }
    },
    handleCategoryChange(categoryId) {
      this.query.categoryId = categoryId
      this.query.pageNum = 1
      this.fetchClasses()
    },
    extractListAndTotal(data) {
      if (!data) return { list: [], total: 0 }
      const list = Array.isArray(data.list)
        ? data.list
        : (Array.isArray(data.records) ? data.records : (Array.isArray(data.items) ? data.items : []))
      const total = data.total == null ? list.length : Number(data.total)
      return { list, total: Number.isNaN(total) ? list.length : total }
    },
    normalizeClass(item, index) {
      const id = (item && (item.id ?? item.classId ?? item.class_id)) ?? index
      const className = (item && (item.className ?? item.class_name ?? item.title ?? item.name)) || ''
      const subtitle = (item && (item.subtitle ?? item.desc ?? item.description)) || ''
      const coverImage = (item && (item.coverImage ?? item.cover_image ?? item.cover ?? item.image)) || ''
      const priceRaw = item && (item.price ?? item.salePrice ?? item.sale_price)
      const price = priceRaw == null ? 0 : Number(priceRaw)
      const priceText = price > 0 ? `¥${price}` : '免费'
      const courseCount = item && (item.courseCount ?? item.course_count) || 0
      const paperCount = item && (item.paperCount ?? item.paper_count) || 0
      const salesCount = item && (item.salesCount ?? item.sales_count) || 0

      return {
        id,
        className,
        subtitle,
        coverImage,
        priceText,
        courseCount: Number(courseCount),
        paperCount: Number(paperCount),
        salesCount: Number(salesCount),
        raw: item
      }
    },
    async fetchClasses() {
      if (this.loading) return
      this.loading = true
      try {
        const params = {
          className: this.query.className || undefined,
          pageNum: this.query.pageNum,
          pageSize: this.query.pageSize
        }
        const res = await getClassList(params)
        const { list, total } = this.extractListAndTotal(res && res.data)
        this.classes = list.map((item, index) => this.normalizeClass(item, index))
        this.total = total
      } catch (err) {
        this.classes = []
        this.total = 0
        this.$message.error((err && err.message) || '班级列表加载失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      const className = String(this.query.className || '').trim()
      if (className === String((this.$route && this.$route.query && this.$route.query.className) || '').trim()) {
        this.query.pageNum = 1
        this.fetchClasses()
        return
      }
      this.$router.push({ name: 'classList', query: { ...(className ? { className } : {}) } })
    },
    handlePageChange(page) {
      this.query.pageNum = page
      this.fetchClasses()
    },
    goDetail(cls) {
      const id = cls && cls.id
      if (id == null || id === '') return
      this.$router.push({ name: 'classDetail', params: { id } })
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; background-color: #f5f7fa; }
.main-content { flex: 1; padding-top: 20px; }

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0 20px;
}

.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0;
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
}

.tab-item {
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
  background: #f5f7fa;
}

.tab-item:hover {
  color: #409eff;
  background: #ecf5ff;
}

.tab-item.active {
  color: #fff;
  background: #409eff;
}

.search-input { width: 360px; }

.list-body { min-height: 240px; }

.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.card {
  cursor: pointer;
}

.thumb {
  width: 100%;
  height: 160px;
  overflow: hidden;
  border-radius: 4px;
  margin-bottom: 12px;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subtitle {
  font-size: 14px;
  color: #909399;
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.price {
  font-size: 18px;
  font-weight: 600;
  color: #f56c6c;
}

.info {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}

.sales {
  font-size: 12px;
  color: #909399;
}

.pager {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
</style>
